import type { ComponentType } from "react";
import { AUTHORS, DEFAULT_AUTHOR } from "./authors";
import { POST_SEO, type PostSeo } from "./post-seo";

export interface PostMeta {
  slug: string;
  title: string;
  description: string;
  pubDate: string; // ISO
  updatedDate?: string;
  authorSlug: string;
  category: string;
  tags: string[];
  readMinutes: number;
  heroEyebrow: string; // short label shown at top of hero
}

export interface PostModule {
  meta: PostMeta;
  Body: ComponentType;
}

// Eager-glob every post file. Each post file exports `meta` and `Body`.
const modules = import.meta.glob<PostModule>("./posts/*.tsx", { eager: true });

// Quality gate (AdSense "low value content" fix). These posts are too thin
// (under ~600 words of real prose) or duplicate another, fuller post. They are
// unpublished: excluded from listings, search, RSS, sitemaps, and they 404.
// Expand a post to 1,200+ original words, then remove its slug to republish.
export const UNPUBLISHED_SLUGS = new Set<string>([
  "du-csas-round-2-schedule-revised-2026",
  "useful-notes-at-revision-time",
  "last-week-revision-macroeconomics",
  "study-timetable-you-actually-follow",
  "best-study-spots-north-campus",
  "honours-vs-programme",
  "what-economics-honours-grads-do",
  "cat-vs-civil-services-vs-masters",
  "du-economics-pyq-analysis-patterns-2020-2026",
  "one-page-cheat-sheets-that-work",
  "public-finance-taxation-fiscal-policy-notes-india",
  "is-ma-economics-worth-it",
  "development-economics-theories-models-indian-policy",
  "how-to-write-economics-essays-research-papers-guide",
  "active-recall-theory-subjects",
  "du-sol-renamed-cdoe-sports-scholarship",
  "mathematical-economics-optimization-lagrangian-notes",
  "du-direct-phd-four-year-ug",
  "du-economics-exam-first-5-minutes",
  "du-economics-semester-wise-syllabus-reading-list-2026",
  "indian-economy-key-data-statistics-cheat-sheet-2026",
  "du-csas-round-2-allotment-2026",
  "du-grading-attendance-explained",
  "inflation-types-causes-wpi-cpi-india-notes",
  "statistics-for-economics-probability-distributions-notes",
  "macroeconomics-formulas-models-quick-reference",
  "theory-of-production-returns-to-scale-cost-curves-notes",
  "balance-of-payments-current-capital-account-india",
  "du-academic-council-qs-skill-courses",
  "where-students-lose-marks-numericals",
  // Duplicates of the fuller Sem 1 unit-wise notes
  "monopoly-price-discrimination-deadweight-loss-du",
  "indifference-curve-budget-line-consumer-equilibrium-notes",
]);

export const POSTS: PostModule[] = Object.values(modules)
  .filter((m) => m && m.meta && !UNPUBLISHED_SLUGS.has(m.meta.slug))
  .sort((a, b) => (a.meta.pubDate < b.meta.pubDate ? 1 : -1));

export function getPost(slug: string): PostModule | undefined {
  return POSTS.find((p) => p.meta.slug === slug);
}

export function postsInCategory(category: string): PostModule[] {
  return POSTS.filter((p) => p.meta.category === category);
}

export function authorFor(slug: string) {
  return AUTHORS[slug] ?? DEFAULT_AUTHOR;
}

export function relatedPosts(slug: string, category: string, limit = 3): PostModule[] {
  const sameCat = POSTS.filter((p) => p.meta.slug !== slug && p.meta.category === category);
  const filler = POSTS.filter((p) => p.meta.slug !== slug && p.meta.category !== category);
  return [...sameCat, ...filler].slice(0, limit);
}

export function getSeo(slug: string): PostSeo | undefined {
  return POST_SEO[slug];
}

export function getPrevNext(slug: string): { prev?: PostMeta; next?: PostMeta } {
  const idx = POSTS.findIndex((p) => p.meta.slug === slug);
  if (idx < 0) return {};
  // POSTS is sorted newest-first: "next" (older) is idx+1, "prev" (newer) is idx-1.
  return {
    prev: idx > 0 ? POSTS[idx - 1].meta : undefined,
    next: idx < POSTS.length - 1 ? POSTS[idx + 1].meta : undefined,
  };
}

export function formatDate(iso: string): string {
  // Deterministic formatter. Do NOT use toLocaleDateString here: the
  // Cloudflare Workers ICU build renders "en-IN" differently than
  // Chromium (e.g. "26 July 2026" vs "26 July, 2026"), which causes a
  // React hydration mismatch (#418) and blanks the page for Googlebot
  // and AdSense's headless renderer.
  const d = new Date(iso);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const day = d.getUTCDate();
  const month = months[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  return `${day} ${month} ${year}`;
}